import { useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in src/components/insurance/ require three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import type { IPolicyApprovalActionsProps } from '@/@types/policyFormTemplates';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Check, X } from 'lucide-react';

export function PolicyApprovalActions({
  policyId,
  orgId,
  applicationValues,
  onApproved,
  onRejected,
}: IPolicyApprovalActionsProps) {
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const approveMutation = useMutation(api.insurance.policyApproval.approveApplication);
  const rejectMutation = useMutation(api.insurance.policyApproval.rejectApplication);

  const benefitPlans = useQuery(api.insurance.benefitPlans.list, { orgId });

  // Try to match the plan name from application values (e.g., values.plan === 'gold' or 'silver')
  const selectedPlanHint = applicationValues?.plan as string | undefined;

  const matchingPlans = benefitPlans?.filter((p: { _id: string; name: string }) => {
    if (!selectedPlanHint) return true;
    return p.name.toLowerCase().includes(selectedPlanHint.toLowerCase());
  });

  const displayedPlans = (matchingPlans ?? benefitPlans) ?? [];

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await approveMutation({
        policyId,
        benefitPlanId: selectedPlanId ? (selectedPlanId as Id<'benefitPlans'>) : undefined, // AS_JUSTIFIED: value sourced from benefitPlans list returned by Convex, guaranteed to be valid Id<'benefitPlans'>
      });
      setShowApproveDialog(false);
      onApproved?.();
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) return;
    setIsRejecting(true);
    try {
      await rejectMutation({
        policyId,
        rejectionReason: rejectionReason.trim(),
      });
      setShowRejectDialog(false);
      onRejected?.();
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <>
      <div className="flex gap-3">
        <Button variant="default" onClick={() => setShowApproveDialog(true)}>
          <Check className="mr-1 h-4 w-4" />
          Approve Application
        </Button>
        <Button variant="destructive" onClick={() => setShowRejectDialog(true)}>
          <X className="mr-1 h-4 w-4" />
          Reject Application
        </Button>
      </div>

      {/* Approve confirmation with optional benefit plan assignment */}
      <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Policy Application</AlertDialogTitle>
            <AlertDialogDescription>
              This will activate the policy and notify the applicant. You can optionally assign a
              benefit plan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Assign Benefit Plan (optional)</Label>
              <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a benefit plan..." />
                </SelectTrigger>
                <SelectContent>
                  {displayedPlans.map((plan: { _id: string; name: string }) => (
                    <SelectItem key={plan._id} value={plan._id}>
                      {plan.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApprove} disabled={isApproving}>
              {isApproving ? 'Approving...' : 'Approve'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject dialog with required reason */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Policy Application</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejection. The applicant will be notified and can revise
              their application.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rejection Reason</Label>
              <Textarea
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                placeholder="Enter the reason for rejection..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={isRejecting || !rejectionReason.trim()}
            >
              {isRejecting ? 'Rejecting...' : 'Reject Application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
